import { Injectable } from '@angular/core';
import { KeepAwakeService } from "./keep-awake.service";
import { Preferences } from '@capacitor/preferences';
import { Hymn } from "../hymn/hymn";
import { hymnsDetailed } from "../hymn/hymnsDetailed";
import { BehaviorSubject, Subject } from "rxjs";
import { ThemeService } from "./theme.service";
import {translation} from "../translation";

interface UserSettings {
  hymnViewMode: string;
  keepAwake: boolean;
  instruments: string[];
  autoscroll: boolean;
  readModeFontSize: number;
  presentationModeVisibleLines: number;
  theme: string;
  themeBackgroundEnabled: boolean;
  themeBlurEnabled: boolean;
  version?: string;
  textOutlineEnabled: boolean;
  textAlignmentReadMode: string;
  // New: separate alignment for presentation mode
  textAlignmentPresentationMode?: string;
  introEnabled: boolean;
  newFeaturesVersion: number;
  playbackTranspose?: number;
  enabledInstruments?: string[]; // instruments that are allowed in selection menus (excludes 'random')
  // Global font family (optional -> default system font when undefined)
  fontFamily?: string;
  // Deprecated per-mode font families (kept for backward-compat in migration)
  readModeFontFamily?: string;
  presentationModeFontFamily?: string;
  // global font weight for text (optional -> default 400)
  fontWeight?: number;

  guessTheHymn: {
    topScoreAllHymns: number;
    topScoreFavoriteHymns: number;
    allHymnMode: boolean;
  }
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  HYMN: string = translation.path_hymn;
  PRESENTATION: string = translation.path_present;

  debuggerConsole = false;

  private settings: UserSettings = {
    hymnViewMode: this.HYMN,
    keepAwake: true,
    instruments: ["random", "harp"],
    autoscroll: false,
    readModeFontSize: 32,
    presentationModeVisibleLines: 4,
    theme: 'lake',
    themeBackgroundEnabled: true,
    themeBlurEnabled: false,
    textOutlineEnabled: true,
    textAlignmentReadMode: 'center',
    textAlignmentPresentationMode: 'center',
    introEnabled: true,
    newFeaturesVersion: 0,
    playbackTranspose: 0,
    enabledInstruments: ["piano","organ","violin","harp","flute","guitar","ensemble","electric_piano","synth","voice","music_box"],
    fontFamily: 'Quicksand',
    fontWeight: 500,

    guessTheHymn: {
      topScoreAllHymns: 0,
      topScoreFavoriteHymns: 0,
      allHymnMode: true
    }
  };

  private favoriteMap = new Map<number, Hymn>();
  private hymnsToLearnMap = new Map<number, Hymn>();
  private historyMap = new Map<number, Hymn>();

  private favorites: Hymn[] = [];
  private hymnsToLearn: Hymn[] = [];
  private history: Hymn[] = [];

  readModeFontSizeSubject = new Subject<number>();
  presentationModeVisibleLinesSubject = new Subject<number>();
  // font family updates (global and legacy per-mode)
  fontFamilySubject = new BehaviorSubject<string | undefined>(undefined);
  // global font weight update
  fontWeightSubject = new BehaviorSubject<number | undefined>(undefined);
  settingsOpened: boolean = false;
  settingsToggleSubject = new BehaviorSubject<boolean>(false);
  settingsState$ = this.settingsToggleSubject.asObservable();

  // subject to wait for the settings to be loaded
  settingsLoaded = new BehaviorSubject<boolean>(false);
  settingsLoaded$ = this.settingsLoaded.asObservable();

  // Enabled instruments handling
  private allInstrumentKeys: string[] = ["piano","organ","violin","harp","flute","guitar","ensemble","electric_piano","synth","voice","music_box"];
  enabledInstrumentsSubject = new BehaviorSubject<string[]>(this.settings.enabledInstruments ?? this.allInstrumentKeys);
  enabledInstruments$ = this.enabledInstrumentsSubject.asObservable();

  // Current instrument selection changes
  instrumentsSubject = new BehaviorSubject<string[]>(this.settings.instruments);
  instruments$ = this.instrumentsSubject.asObservable();

  constructor(private keepAwakeService: KeepAwakeService, private themeService: ThemeService) {
    this.init();
  }

  // Available font families declared in assets/fonts/font-file.scss (names must match @font-face)
  getAvailableFonts(): string[] {
    return [
      'Carattere',
      'Lato',
      'Newsreader',
      'PatrickHand',
      'Poppins',
      'Raleway',
      'Quicksand'
    ];
  }

  // Global font family API
  getFontFamily(): string | undefined {
    return this.settings.fontFamily;
  }

  setFontFamily(fontFamily: string | undefined) {
    this.settings.fontFamily = fontFamily;
    this.fontFamilySubject.next(fontFamily);
    this.saveSettings();
  }

  setPresentationModeFontFamily(fontFamily: string | undefined) {
    this.setFontFamily(fontFamily);
  }

  getFontWeight(): number | undefined {
    return this.settings.fontWeight;
  }

  setFontWeight(weight: number | undefined) {
    this.settings.fontWeight = weight;
    this.fontWeightSubject.next(weight);
    this.saveSettings();
  }

  private async writePreference(key: string, value: any) {
    await Preferences.set({ key: key, value: value });
  };

  async init() {

    console.debug('Initializing settings');
    await this.loadSettings();
    await this.loadFavorites();
    await this.loadHymnsToLearn();
    await this.loadHistory();
    this.settingsLoaded.next(true);
  }

  /**
   * Wait for the settings to be loaded
   */
  async waitForSettings(): Promise<void> {
    return new Promise((resolve) => {
      if (this.settingsLoaded.value) {
        resolve();
      }
      else {
        this.settingsLoaded$.subscribe(() => {
          resolve();
        });
      }
    });
  }

  async loadSettings() {
    const storedSettings = await Preferences.get({ key: translation.app_settings_key });
    if (storedSettings.value) {
      this.settings = { ...this.settings, ...JSON.parse(storedSettings.value) };
    }
    // Migrate legacy per-mode font settings to global fontFamily if needed
    if (this.settings.fontFamily === undefined) {
      const legacy = this.settings.readModeFontFamily ?? this.settings.presentationModeFontFamily;
      if (legacy !== undefined) {
        this.settings.fontFamily = legacy;
      }
    }
    // Backward compatibility: ensure enabledInstruments populated
    if (!this.settings.enabledInstruments || this.settings.enabledInstruments.length === 0) {
      this.settings.enabledInstruments = [...this.allInstrumentKeys];
    }
    // Backward compatibility: ensure presentation text alignment is present
    if (!this.settings.textAlignmentPresentationMode) {
      this.settings.textAlignmentPresentationMode = this.settings.textAlignmentReadMode ?? 'center';
    }

    // Emit current enabled instruments to subscribers
    this.enabledInstrumentsSubject.next([...this.settings.enabledInstruments!]);
    // Emit instruments selection as well (for any dynamic listeners)
    this.instrumentsSubject.next([...this.settings.instruments]);

    this.applySettings();
  }

  async saveSettings() {
    await Preferences.set({ key: translation.app_settings_key, value: JSON.stringify(this.settings) });
  }

  applySettings() {
    this.readModeFontSizeSubject.next(this.settings.readModeFontSize);
    this.presentationModeVisibleLinesSubject.next(this.settings.presentationModeVisibleLines);
    // emit current font families (may be undefined for system default)
    this.fontFamilySubject.next(this.settings.fontFamily);
    // emit current global font weight
    this.fontWeightSubject.next(this.settings.fontWeight);
    if(this.settings.keepAwake) {
      this.keepAwakeService.enable();
    }
    this.reloadTheme();
  }

  async loadFavorites() {
    const storedFavorites = await Preferences.get({ key: translation.app_base_key + ".favorites" });
    if (storedFavorites.value) {
      this.favorites = JSON.parse(storedFavorites.value).map((num: number) => hymnsDetailed[num - 1]);
      this.favorites.forEach(hymn => this.favoriteMap.set(hymn.hymnNumber, hymn));
    }
  }

  async loadHymnsToLearn() {
    const storedHymnsToLearn = await Preferences.get({ key: translation.app_base_key + ".hymnsToLearn" });
    if (storedHymnsToLearn.value) {
      this.hymnsToLearn = JSON.parse(storedHymnsToLearn.value).map((num: number) => hymnsDetailed[num - 1]);
      this.hymnsToLearn.forEach(hymn => this.hymnsToLearnMap.set(hymn.hymnNumber, hymn));
    }
  }

  async loadHistory() {
    const storedHistory = await Preferences.get({ key: `${translation.app_base_key}.history` });
    if (storedHistory.value) {
      this.history = JSON.parse(storedHistory.value).map((num: number) => hymnsDetailed[num - 1]);
      this.history.forEach(hymn => this.historyMap.set(hymn.hymnNumber, hymn));
    }
  }

  toggleSettings() {
    this.settingsOpened = !this.settingsOpened;
    this.settingsToggleSubject.next(!this.settingsToggleSubject.value);
  }

  setFontSize(fontSize: number) {
    this.settings.readModeFontSize = fontSize;
    this.readModeFontSizeSubject.next(fontSize);
    this.saveSettings();
  }

  setPresentationModeVisibleLines(visibleLines: number) {
    this.settings.presentationModeVisibleLines = visibleLines;
    this.presentationModeVisibleLinesSubject.next(visibleLines);
    this.saveSettings();
  }

  getPresentationModeVisibleLines() {
    return this.settings.presentationModeVisibleLines;
  }

  setTheme(theme: string) {
    this.settings.theme = theme;
    this.themeService.applyTheme(theme, this.getThemeBackgroundEnabled(), this.getThemeBlurEnabled());
    this.saveSettings();
  }

  reloadTheme() {
    this.themeService.applyTheme(
      this.settings.theme,
      this.getThemeBackgroundEnabled(),
      this.getThemeBlurEnabled(),
      this.getTextOutlineEnabled(),
      this.getTextAlignmentReadMode(),
      this.getTextAlignmentPresentationMode()
    );
  }

  getFavorites(): Hymn[] {
    return this.favorites;
  }

  getHymnsToLearn(): Hymn[] {
    return this.hymnsToLearn;
  }

  getHistory(): Hymn[] {
    return this.history;
  }

  getHymnViewMode(): string {
    return this.settings.hymnViewMode;
  }

  getInstruments(): string[] {
    return this.settings.instruments;
  }

  // Enabled instruments API
  getAllInstrumentKeys(): string[] {
    return [...this.allInstrumentKeys];
  }

  getEnabledInstruments(): string[] {
    return this.settings.enabledInstruments ?? [...this.allInstrumentKeys];
  }

  isInstrumentEnabled(key: string): boolean {
    return (this.settings.enabledInstruments ?? this.allInstrumentKeys).includes(key);
  }

  private ensureAtLeastOneEnabled(next: string[]): string[] {
    if (next.length === 0) {
      // fallback: enable piano
      return ['piano'];
    }
    return next;
  }

  async setEnabledInstruments(keys: string[]) {
    const next = this.ensureAtLeastOneEnabled(keys.filter(k => this.allInstrumentKeys.includes(k)));
    this.settings.enabledInstruments = next;
    this.enabledInstrumentsSubject.next([...next]);
    // If selected instruments are not enabled anymore, fix them
    await this.fixSelectedInstrumentsIfDisabled();
    await this.saveSettings();
  }

  async setInstrumentEnabled(key: string, enabled: boolean) {
    let current = new Set(this.getEnabledInstruments());
    if (enabled) {
      current.add(key);
    } else {
      current.delete(key);
    }
    await this.setEnabledInstruments(Array.from(current));
  }

  private async fixSelectedInstrumentsIfDisabled() {
    const enabled = new Set(this.getEnabledInstruments());
    let [lead, acc] = this.getInstruments();
    let changed = false;
    if (lead && !enabled.has(lead) && lead !== 'random') {
      lead = 'random';
      changed = true;
    }
    if (acc && !enabled.has(acc)) {
      acc = undefined as any; // remove accompaniment
      changed = true;
    }
    const nextInstruments: string[] = lead ? [lead] : [];
    if (acc) nextInstruments.push(acc);
    if (changed) {
      await this.setInstruments(nextInstruments);
    }
  }

  getPlaybackTranspose(): number {
    return this.settings.playbackTranspose ?? 0;
  }

  async setPlaybackTranspose(semitones: number) {
    this.settings.playbackTranspose = semitones;
    await this.saveSettings();
  }

  async setHymnViewMode() {
    this.settings.hymnViewMode = this.HYMN;
    await this.saveSettings();
  }

  async setPresentationViewMode() {
    this.settings.hymnViewMode = this.PRESENTATION;
    await this.saveSettings();
  }

  async setInstruments(instruments: string[]) {
    this.settings.instruments = instruments;
    this.instrumentsSubject.next([...this.settings.instruments]);
    await this.saveSettings();
  }

  getAutoscroll() {
    return this.settings.autoscroll;
  }

  async setAutoscroll(autoscroll: boolean) {
    this.settings.autoscroll = autoscroll;
    await this.saveSettings();
  }

  clearHymnsToLearn() {
    this.hymnsToLearnMap.clear();
    this.updateSavedHymns();
  }

  clearFavorites() {
    this.favoriteMap.clear();
    this.updateSavedHymns();
  }

  updateSavedHymns() {
    this.favorites = Array.from(this.favoriteMap.values());
    this.hymnsToLearn = Array.from(this.hymnsToLearnMap.values());
    this.writePreference(`${translation.app_base_key}.hymnsToLearn`, JSON.stringify(this.hymnsToLearn.map((hymn: Hymn) => hymn.hymnNumber)));
    this.writePreference(`${translation.app_base_key}.favorites`, JSON.stringify(this.favorites.map((hymn: Hymn) => hymn.hymnNumber)));
  }

  addToHistory(hymnNumber: number) {
    console.debug('Adding to history: ' + hymnNumber);
    if (this.historyMap.has(hymnNumber)) {
      this.historyMap.delete(hymnNumber);
    }
    this.historyMap.set(hymnNumber, hymnsDetailed[hymnNumber-1]);
    this.updateHistory();
  }

  updateHistory() {
    this.history = Array.from(this.historyMap.values()).reverse();
    this.history.length = Math.min(30, this.history.length);
    this.writePreference(`${translation.app_base_key}.history`, JSON.stringify(this.history.map((hymn: Hymn) => hymn.hymnNumber)));
  }

  getReadModeFontSize() {
    return this.settings.readModeFontSize;
  }

  async getVersion() {
    return this.settings.version;
  }

  async setVersion(version: string) {
    this.settings.version = version;
    await this.saveSettings();
  }

  isFavorite(hymnNumber: number): boolean {
    return this.favoriteMap.has(hymnNumber);
  }

  isHymnToLearn(hymnNumber: number): boolean {
    return this.hymnsToLearnMap.has(hymnNumber);
  }

  isFavoriteOrHymnToLearn(hymnNumber: number): boolean {
    return this.isFavorite(hymnNumber) || this.isHymnToLearn(hymnNumber);
  }

  toggleFavorite(hymn: any): void {
    console.debug('Toggling favorite: ' + hymn.hymnNumber);
    if (this.isFavorite(hymn.hymnNumber)) {
      this.favoriteMap.delete(hymn.hymnNumber);
    }
    else {
      this.hymnsToLearnMap.delete(hymn.hymnNumber);
      this.favoriteMap.set(hymn.hymnNumber, hymn);
    }
    this.updateSavedHymns();
  }

  toggleHymnToLearn(hymn: any): void {
    console.debug('Toggling hymn to learn: ' + hymn.hymnNumber);
    if (this.isHymnToLearn(hymn.hymnNumber)) {
      this.hymnsToLearnMap.delete(hymn.hymnNumber);
    }
    else {
      this.favoriteMap.delete(hymn.hymnNumber);
      this.hymnsToLearnMap.set(hymn.hymnNumber, hymn);
    }
    this.updateSavedHymns();
  }

  removeHymnFromFavoritesAndToLearn(hymn: any) {
    this.hymnsToLearnMap.delete(hymn.hymnNumber);
    this.favoriteMap.delete(hymn.hymnNumber);

    this.updateSavedHymns();
  }

  getThemeBackgroundEnabled() {
    return this.settings.themeBackgroundEnabled;
  }

  async setThemeBackgroundEnabled(enabled: boolean) {
    this.settings.themeBackgroundEnabled = enabled;
    await this.saveSettings();
    this.reloadTheme();
  }

  getThemeBlurEnabled() {
    return this.settings.themeBlurEnabled;
  }

  async setThemeBlurEnabled(enabled: boolean) {
    this.settings.themeBlurEnabled = enabled;
    await this.saveSettings();
    this.reloadTheme();
  }

  getTextOutlineEnabled() {
    return this.settings.textOutlineEnabled;
  }

  async setTextOutlineEnabled(enabled: boolean) {
    this.settings.textOutlineEnabled = enabled;
    await this.saveSettings();
    this.reloadTheme();
  }

  getTextAlignmentReadMode() {
    return this.settings.textAlignmentReadMode;
  }

  async setTextAlignmentReadMode(textAlignment: string) {
    this.settings.textAlignmentReadMode = textAlignment;
    await this.saveSettings();
    this.reloadTheme();
  }

  getTextAlignmentPresentationMode() {
    return this.settings.textAlignmentPresentationMode ?? this.settings.textAlignmentReadMode;
  }

  async setTextAlignmentPresentationMode(textAlignment: string) {
    this.settings.textAlignmentPresentationMode = textAlignment;
    await this.saveSettings();
    this.reloadTheme();
  }

  getIntroEnabled() {
    return this.settings.introEnabled;
  }

  async setIntroEnabled(enabled: boolean) {
    this.settings.introEnabled = enabled;
    await this.saveSettings();
  }

  getKeepAwake() {
    return this.settings.keepAwake;
  }

  async setKeepAwake(enabled: boolean) {
    this.settings.keepAwake = enabled;
    await this.saveSettings();
    if(enabled) {
      await this.keepAwakeService.enable();
    } else {
      await this.keepAwakeService.disable();
    }
  }

  clearHistory() {
    this.historyMap.clear();
    this.updateHistory();
  }

  getNewFeaturesVersion() {
    return this.settings.newFeaturesVersion;
  }

  getTopScoreAllHymns() {
    return this.settings.guessTheHymn.topScoreAllHymns?? 0;
  }

  getTopScoreFavoriteHymns() {
    return this.settings.guessTheHymn.topScoreFavoriteHymns?? 0;
  }

  async setTopScoreAllHymns(topScore: number) {
    this.settings.guessTheHymn.topScoreAllHymns = topScore;
    await this.saveSettings();
  }

  async setTopScoreFavoriteHymns(topScore: number) {
    this.settings.guessTheHymn.topScoreFavoriteHymns = topScore;
    await this.saveSettings();
  }

  getGuessTheHymnAllHymnsEnabled() {
    return this.settings.guessTheHymn.allHymnMode;
  }

  async setGuessTheHymnAllHymnsEnabled(enabled: boolean) {
    this.settings.guessTheHymn.allHymnMode = enabled;
    await this.saveSettings();
  }

  async setNewFeaturesVersion(version: number) {
    this.settings.newFeaturesVersion = version;
    await this.saveSettings();
  }
}
